import { waitFor } from 'xstate';

import { fetchEndpointData } from 'api';
import type { Cart, CartRequest, VerifyPaymentRequest } from 'models/api';
import type { UserInfoMachineActor } from 'state-machines/checkout/userInfo';
import type { CartId } from 'types';
import {
	type CheckoutUpdatableCartProperties,
	formatCartForRequest,
} from 'utils/business-logic';

export function fetchNewCart() {
	return fetchEndpointData('PostCart');
}

export function fetchSpecificCart(cartId: CartId | undefined) {
	if (!cartId) {
		throw new Error('CartId missing');
	}
	return fetchEndpointData({
		name: 'GetCart',
		pathParams: { cartId },
	});
}

export function updateCart(
	cart: Cart | undefined,
	updatedCartProperties: CheckoutUpdatableCartProperties | undefined,
) {
	if (!cart) {
		throw new Error('Cart is undefined');
	}
	const updatedCart: CartRequest = formatCartForRequest(
		cart,
		updatedCartProperties,
	);

	return fetchEndpointData({
		name: 'PutCart',
		body: updatedCart,
		pathParams: { cartId: cart.id },
	});
}

/**
 * Verify redirect payment
 * - Verifies a redirect payment and returns the full cart.
 * - @see: https://apigw-fe-dev01.juladev.se/api/v1/docs/index.html#operations-Cart-post_api_v1_Cart__cartId__verifyredirectpayment
 */
export function verifyRedirectPayment(
	cartId: CartId | undefined,
	payload: VerifyPaymentRequest | undefined,
) {
	if (!cartId) {
		throw new Error('CartId missing');
	}
	return fetchEndpointData({
		name: 'VerifyRedirectPayment',
		pathParams: { cartId },
		body: payload,
	});
}

/**
 * Copy cart
 * - Copies cart (e.g. after requesting /Cart/checkout the cart is locked and can't be modified,
 *   so if a payment failes we need to copy it to be able to make changes to it).
 * - @see: https://apigw-fe-dev01.juladev.se/api/v1/docs/index.html#operations-Cart-post_api_v1_Cart__cartId__copy
 */
export function copyCart(cartId: CartId | undefined) {
	if (!cartId) {
		throw new Error('CartId missing');
	}
	return fetchEndpointData({
		name: 'PostCopyCart',
		pathParams: { cartId },
	});
}

export function checkoutCart(cart: Cart | undefined) {
	if (!cart) {
		throw new Error('Cart is undefined');
	}
	const updatedCart: CartRequest = formatCartForRequest(cart);

	return fetchEndpointData({
		name: 'PostCheckoutCart',
		body: updatedCart,
	});
}

export function waitForUserInfoActor(actor: UserInfoMachineActor) {
	return waitFor(actor, (state) => state.matches('idle'), {
		timeout: 120_000,
	});
}
